import React from 'react';
import styled from 'styled-components';
import { TextLink } from '@oneaudi/unified-web-components';
import {
  getClassNamesForLinks,
  getIdNameForLink,
  getLinkTarget,
  onCookieModalLinkClick,
} from '../../services/helpers';
import type { TrackingClickEvent } from '../../utils/tracking';
import { useTracking } from '../../utils/tracking';
import type { Link } from '../../types/audi-footer-response.types';
import { TrackingContext } from '../../context';
import { SYS_SPACE_FIXED_100, SYS_SPACE_FIXED_200 } from '@oneaudi/unified-web-common';

type LegalLinksProps = {
  countryCode?: string;
  links?: Link[];
  openLayer: React.MouseEventHandler<HTMLAnchorElement>;
  useOneLayer?: boolean;
};

const LegalLinksStyledWrapper = styled.div`
  flex-grow: 1;
  margin-right: var(--one-footer-space-xl);
  .legal-link {
    color: var(--one-footer-color-white);
  }

  & > ul {
    display: inline-flex;
    flex-flow: row wrap;
    list-style: none;

    padding: 0;
  }

  & > ul > li {
    padding-block: ${SYS_SPACE_FIXED_100};
  }

  & > ul > li:not(:last-child) {
    padding-right: ${process.env.IS_LEGACY ? 'var(--one-footer-space-l)' : SYS_SPACE_FIXED_200};
  }

  & > ul > li > a {
    display: inline-block;
  }
`;

const LegalLinks: React.FC<LegalLinksProps> = ({ countryCode, links, openLayer, useOneLayer }) => {
  const trackingService = React.useContext(TrackingContext);
  const { sendClickEvent } = useTracking(trackingService);
  const sendButtonClickEvent = (
    label: string,
    url: string,
    variant: string,
    openin: string,
    position: string,
  ) => {
    const event: TrackingClickEvent = {
      linkLabel: label,
      linkUrl: url,
      elementName: variant,
      headline: 'Bottom Bar',
      OpenIn: openin,
      position,
    };

    sendClickEvent(event);
  };
  const handleClick = (
    event: React.MouseEvent<HTMLAnchorElement>,
    link: Link,
    index: string,
  ): void => {
    const target = getLinkTarget(link, useOneLayer);
    const { Type, Text, Url, Target } = link;
    if (!!Type && Type === 'cookie-settings-layer') {
      onCookieModalLinkClick(event);
    } else if (Target === '_layer' && useOneLayer) {
      openLayer(event);
    }
    sendButtonClickEvent(Text, Url, 'text link', target, index);
  };
  return (
    <LegalLinksStyledWrapper>
      <ul>
        {links?.map((link) => {
          return (
            <li key={`${link.Target}${link.Text}${link.Url}`}>
              <TextLink
                className={`${getClassNamesForLinks(link, countryCode, useOneLayer)} legal-link`}
                onClick={(event) => {
                  handleClick(event, link, '');
                }}
                href={link.Url}
                rel="noreferrer"
                size="sm"
                variant="standalone"
                target={getLinkTarget(link, useOneLayer)}
                {...(link.AriaLabel ? { 'aria-label': link.AriaLabel } : {})}
                {...(getIdNameForLink(link) ? { id: getIdNameForLink(link) } : {})}
              >
                {link.Text}
              </TextLink>
            </li>
          );
        })}
      </ul>
    </LegalLinksStyledWrapper>
  );
};

export default LegalLinks;
